import { useEffect } from "react";
import CookieConsent from "@/components/CookieConsent";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
  type ErrorComponentProps,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import inter400 from "@fontsource/inter/files/inter-latin-400-normal.woff2?url";
import inter700 from "@fontsource/inter/files/inter-latin-700-normal.woff2?url";
import { LanguageProvider } from "@/contexts/LanguageContext";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { getAnalyticsConfig } from "@/lib/analytics-config.functions";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  loader: () => getAnalyticsConfig(),
  staleTime: Infinity,
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "theme-color", content: "#E84C1E" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      // Google Search Console verification — preserved from the old index.html
      { name: "google-site-verification", content: "xI1wYe4ZRJ26EHE1SsTkqaiLPEBr7iznEhQODbAiITs" },
      { title: "Page introuvable | CMO Partner" },
      { property: "og:site_name", content: "CMO Partner" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "preload", as: "font", type: "font/woff2", href: inter700, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: inter400, crossOrigin: "anonymous" },
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/favicon-96x96.png" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
    ],
    // Marks JS as active so below-the-fold reveal effects may hide content; fallback reveals after 3 s.
    scripts: [
      {
        children:
          "document.documentElement.classList.add('js');setTimeout(function(){document.querySelectorAll('[data-reveal]').forEach(function(n){n.classList.add('is-visible')})},3000);",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="fr" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const analytics = Route.useLoaderData();

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <CookieConsent measurementId={analytics?.measurementId ?? null} />
        <Outlet />
      </LanguageProvider>
    </QueryClientProvider>
  );
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();

  console.error(error);

  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-bold text-foreground">This page didn't load</h1>
        <p className="mt-3 text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            className="rounded-lg bg-primary px-5 py-2.5 font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            onClick={() => {
              void router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a
            href="/"
            className="rounded-lg border border-border px-5 py-2.5 font-semibold text-foreground transition-colors hover:border-primary"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
